import "./Login.css";

import {
  LoginStatus,
  LoginValues,
  checkStatus,
  login,
  resetPassword,
} from "./Login.service";
import { RootState, useAppDispatch, useAppSelector } from "../../store";

import React from "react";
import { logoutAppUserThunk } from "../../store/slices/userThunks";
import { userActions } from "../../store/slices/userSlice";

interface LoginLayout {
  title: string;
  items: React.ReactElement[];
  buttons: React.ReactElement[];
  func: () => void;
}

interface LoginProps {
  logout?: boolean;
}

export default function Login(props: LoginProps) {
  const urlParams = new URLSearchParams(window.location.search);

  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [commonMessage, setCommonMessage] = React.useState("");
  const user = useAppSelector((state: RootState) => state.user.user);
  const error = useAppSelector((state: RootState) => state.user.error);
  const initial: LoginValues = {
    name: "",
    email: urlParams.get("email") || "",
    pass: "",
    verify: "",
  };
  const [values, setValues] = React.useState<LoginValues>(initial);
  const [status, setStatus] = React.useState<LoginStatus>({
    name: "virgin",
    email: "virgin",
    pass: "virgin",
    verify: "virgin",
  });

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (user) {
      const path = urlParams.get("redirect") || "/lessons";
      window.location.assign(path);
    }
  }, [user, urlParams]);

  React.useEffect(() => {
    setErrorMessage(error?.message ? error.message : error?.toString() || null);
    setStatus((prev) => {
      const value = { ...prev };
      switch (error?.code) {
        case "auth/email-already-exists":
        case "auth/invalid-email":
          value.email = "bad";
          break;

        case "auth/wrong-password":
        case "auth/invalid-password":
          value.pass = "bad";
          break;
      }
      return value;
    });
  }, [error]);

  const { logout } = props;

  if (logout) {
    dispatch(logoutAppUserThunk());
    return <div className="login-page">Logging out...</div>;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommonMessage("");
    dispatch(userActions.clearErrors());
    const newValues = { ...values, [e.target.name]: e.target.value };
    setValues(newValues);
    setStatus(checkStatus(newValues));
  };

  const noop = () => {};
  const checkCode = () => {};
  const pass2text = () => {};

  const doLogin = () => {
    login(values.email, values.pass, dispatch);
  };

  const submit = (evt: any) => {
    switch (mode) {
      case "create":
        // createUser()
        break;
      case "set":
      case "reset":
      case "resetPassword":
        const oobCode = urlParams.get("oobCode");
        resetPassword(oobCode, values.pass)
          .then(doLogin)
          .catch((err) => dispatch(userActions.setError(err)));
        break;
      default:
        doLogin();
    }
  };

  const ItemName = (
    <div key="name" className="form-item form-item-name">
      <div className="form-label">Name</div>
      <div className="form-input">
        <input
          name="name"
          type="text"
          value={values.name}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const ItemEmail = (
    <div key="email" className="form-item form-item-email">
      <div className="form-label">Email</div>
      <div className="form-input">
        <input
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
        />
        {status.email === "good" && (
          <div className="form-input-ok">
            <i className="fas fa-check" id="email-ok" aria-hidden="true"></i>
          </div>
        )}
        {status.email === "bad" && (
          <div className="form-input-not-ok">
            <i className="fas fa-ban" id="email-not-ok" aria-hidden="true"></i>
          </div>
        )}
      </div>
    </div>
  );

  const ItemPassword = (
    <div key="pass" className="form-item form-item-pass">
      <div className="form-label">Password</div>
      <div className="form-input">
        <input
          name="pass"
          type="password"
          value={values.pass}
          onChange={handleChange}
        />
        {status.pass === "good" && (
          <div className="form-input-ok">
            <i className="fas fa-check" id="pass-ok" aria-hidden="true"></i>
          </div>
        )}
        {status.pass === "bad" && (
          <div className="form-input-not-ok">
            <i className="fas fa-ban" id="pass-not-ok" aria-hidden="true"></i>
          </div>
        )}
      </div>
    </div>
  );

  const ItemVerify = (
    <div key="verify" className="form-item form-item-veri">
      <div className="form-label">Verify Password</div>
      <div className="form-input">
        <input
          name="verify"
          type="password"
          value={values.verify}
          onChange={handleChange}
        />
        {status.verify === "good" && (
          <div className="form-input-ok">
            <i className="fas fa-check" id="veri-ok" aria-hidden="true"></i>
          </div>
        )}
        {status.verify === "bad" && (
          <div className="form-input-not-ok">
            <i className="fas fa-ban" id="veri-not-ok" aria-hidden="true"></i>
          </div>
        )}
      </div>
    </div>
  );

  const ButtonRequest = (
    <div key="request" className="form-button">
      <div className="loa-button loa-button-mini">Reset/Request Password</div>
    </div>
  );

  const ButtonSubmit = (
    <div key="submit" className="form-button">
      <div onClick={submit} className="loa-button loa-button-mini">
        Submit
      </div>
    </div>
  );

  const layouts: Record<string, LoginLayout> = {
    set: {
      title: "Set Password",
      items: [ItemEmail, ItemPassword],
      buttons: [ButtonSubmit],
      func: pass2text,
    },
    reset: {
      title: "Reset Password",
      items: [ItemEmail, ItemPassword],
      buttons: [ButtonSubmit],
      func: checkCode,
    },
    resetPassword: {
      // firebase auth compliant
      title: "Reset Password",
      items: [ItemEmail, ItemPassword],
      buttons: [ButtonSubmit],
      func: checkCode,
    },
    request: {
      title: "Request or Reset Password",
      items: [ItemEmail],
      buttons: [ButtonRequest],
      func: noop,
    },
    create: {
      title: "Create New User",
      items: [ItemName, ItemEmail, ItemPassword, ItemVerify],
      buttons: [ButtonSubmit],
      func: noop,
    },
    login: {
      title: "Login",
      items: [ItemEmail, ItemPassword],
      buttons: [ButtonSubmit],
      func: noop,
    },
  };

  const mode = urlParams.get("mode") || "login";
  const layout = layouts[mode];

  // console.log({ user, error, status });

  return (
    <div className="loa-page login-page">
      <div className="form-wrapper">
        <div className="form-title">{layout.title}</div>
        {/* <div className="spinner-container">
          <i className="fas fa-spin fa-spinner"></i>
        </div> */}
        <div className="form-container">
          <form id="user-form">{layout.items}</form>
          <div
            className={`form-buttons ${
              layout.buttons.length === 1 ? "single-button" : ""
            }`}
          >
            {layout.buttons}
          </div>
        </div>
        {errorMessage && (
          <div className="message error-message">{errorMessage}</div>
        )}
        {commonMessage && <div className="message">{commonMessage}</div>}
      </div>
    </div>
  );
}
